import { useState } from 'react';
import { Box, Card, CardContent, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { IndicatorHelp } from '../common/IndicatorHelp';

/**
 * KPI "ticker" (estilo relatório de bolsa de valores): valor do período,
 * pílula ▲/▼ com a variação vs. período anterior de mesma duração, sparkline
 * diária com linha tracejada de abertura.
 * Ref: `ui-ux/prototipo-relatorio-produtos-kpis.html`.
 *
 * `kind`: 'money' (centavos), 'pct' (pontos-base, variação em p.p.),
 * 'days' (dias, 1 casa), 'ratio' ("1,25x") ou 'units'. `inverse`: subir é
 * ruim (custo) — só troca a cor, não a seta. `neutral`: subir não é bom nem
 * ruim — variação em azul. `estimated`: etiqueta "est." (série reconstruída).
 * `cumulative`: `data.series` é diária; a sparkline mostra o acumulado e o
 * tooltip traz também o valor do dia.
 * Sem `data` (ex.: indicadores de snapshot, sem histórico) mostra só o valor.
 * `hideSpark`: indicador de posição (sem série diária) — mantém a variação, sem sparkline;
 * `valueText`: texto que substitui o valor formatado (ex.: "72% / 28%").
 * `hero`: borda esquerda de 4px (destaque). `note`: rodapé tracejado com contexto
 * extra (ex.: margem, taxa), só com tendência. Ref vendas: `ui-ux/prototipo-relatorio-vendas-kpis.html`.
 * Ref estoque: `ui-ux/prototipo-relatorio-estoque-kpis.html`.
 */

const fmtInt = (n) => Math.round(n).toLocaleString('pt-BR');

const FORMATTERS = {
    money: (cents) => {
        const abs = (Math.abs(cents) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return cents < 0 ? `-R$ ${abs}` : `R$ ${abs}`;
    },
    pct: (bp) => `${(bp / 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
    units: fmtInt,
    // dias com 1 casa decimal (ex.: intervalo médio entre compras)
    days: (d) => `${d.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} dias`,
    // razão com 2 casas (ex.: giro de estoque "1,25x")
    ratio: (v) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`,
    // duração em minutos ("45 min", "2 h", "1 h 30 min")
    minutes: (min) => {
        const m = Math.round(min);
        if (m < 60) return `${m} min`;
        const h = Math.floor(m / 60);
        const r = m % 60;
        return r ? `${h} h ${r} min` : `${h} h`;
    },
};

const SIGN = { 1: '+', '-1': '−', 0: '' };

// Direção (1 / -1 / 0) e rótulo da variação; margem usa p.p., o resto usa %.
function describeDelta(kind, total, previous) {
    const diff = total - previous;
    if (kind === 'pct') {
        const pp = Math.abs(diff) / 100;
        const dir = pp < 0.05 ? 0 : diff > 0 ? 1 : -1;
        return { dir, label: `${SIGN[dir]}${pp.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} p.p.`, diff };
    }
    if (!previous) {
        // sem base de comparação: só há variação se o período atual teve movimento
        const dir = diff === 0 ? 0 : diff > 0 ? 1 : -1;
        return { dir, label: dir === 0 ? '0,0%' : 'novo', diff };
    }
    const rel = (diff / Math.abs(previous)) * 100;
    const dir = Math.abs(rel) < 0.05 ? 0 : diff > 0 ? 1 : -1;
    return { dir, label: `${SIGN[dir]}${Math.abs(rel).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`, diff };
}

const W = 200;
const H = 34;
const PAD = 4;

function Sparkline({ series, daily, days, color, bg, format, uid, height = H }) {
    const [hover, setHover] = useState(null);
    if (!series || series.length < 2) return <Box sx={{ height }} />;

    const mn = Math.min(...series);
    const mx = Math.max(...series);
    const range = mx - mn || 1;
    const x = (i) => (i / (series.length - 1)) * W;
    const y = (v) => height - PAD - ((v - mn) / range) * (height - PAD * 2);
    const pts = series.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    const last = [x(series.length - 1), y(series[series.length - 1])];
    const base = y(series[0]).toFixed(1);

    const onMove = (ev) => {
        const r = ev.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
        setHover({ i: Math.round(ratio * (series.length - 1)), px: ratio * r.width, width: r.width });
    };

    const hoverDay = hover && days?.[hover.i] ? days[hover.i].split('-').reverse().slice(0, 2).join('/') : '';

    return (
        <Box
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            sx={{ position: 'relative', height, mx: '-4px', mt: height > H ? '10px' : '4px', mb: height > H ? '12px' : '4px' }}
        >
            <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}>
                <defs>
                    <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor={color} stopOpacity=".28" />
                        <stop offset="1" stopColor={color} stopOpacity="0" />
                    </linearGradient>
                </defs>
                <line x1="0" x2={W} y1={base} y2={base} stroke="currentColor" strokeOpacity=".4" strokeDasharray="3 3" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                <polygon points={`0,${height} ${pts.join(' ')} ${W},${H}`} fill={`url(#${uid})`} />
                <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
            <Box
                sx={{
                    position: 'absolute',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: color,
                    boxShadow: `0 0 0 3px ${bg}`,
                    left: `calc(${(last[0] / W) * 100}% - 3.5px)`,
                    top: `calc(${(last[1] / height) * 100}% - 3.5px)`,
                }}
            />
            {hover && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -4,
                        left: Math.max(45, Math.min(hover.width - 45, hover.px)),
                        transform: 'translateX(-50%)',
                        bgcolor: 'text.primary',
                        color: 'background.paper',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        px: '7px',
                        py: '3px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 2,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {hoverDay} · {format(series[hover.i])}{daily ? ` (dia: ${format(daily[hover.i])})` : ''}
                </Box>
            )}
        </Box>
    );
}

// Pílula ▲/▼ isolada (mesma regra de cor/direção do card) — usada fora do card,
// ex.: linha "Descontos concedidos" do hero do Dashboard de Vendas.
export function KpiDeltaChip({ kind, inverse = false, neutral = false, data }) {
    const theme = useTheme();
    if (!data) return null;
    const delta = describeDelta(kind, data.total, data.previous_total);
    const tone = delta.dir === 0 ? 'flat' : neutral ? 'neutral' : (delta.dir > 0) !== inverse ? 'good' : 'bad';
    const palette = {
        good: { fg: theme.palette.success.main, bg: theme.palette.success.bg },
        bad: { fg: theme.palette.error.main, bg: theme.palette.error.bg },
        neutral: { fg: theme.palette.primary.main, bg: theme.palette.info.bg },
        flat: { fg: theme.palette.text.secondary, bg: theme.palette.action.hover },
    }[tone];
    const arrow = delta.dir === 0 ? '▬' : delta.dir > 0 ? '▲' : '▼';
    return (
        <Box title="vs. período anterior" sx={{ fontSize: '10.5px', fontWeight: 700, px: '7px', py: '1px', borderRadius: '20px', color: palette.fg, bgcolor: palette.bg, display: 'inline-flex', gap: '3px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {arrow} {delta.label}
        </Box>
    );
}

export default function KpiTickerCard({ uid, icon, iconBg, iconFg, label, kind, inverse = false, neutral = false, estimated = false, estimatedHint, cumulative = false, hero = false, large = false, footer, note, data, days, value, valueText, hideSpark = false, help, loading }) {
    const theme = useTheme();
    const format = FORMATTERS[kind] || fmtInt;
    const hasTrend = !!data;
    const total = hasTrend ? data.total : value;

    const delta = hasTrend ? describeDelta(kind, data.total, data.previous_total) : null;
    const tone = !delta || delta.dir === 0 ? 'flat' : neutral ? 'neutral' : (delta.dir > 0) !== inverse ? 'good' : 'bad';
    const palette = {
        good: { fg: theme.palette.success.main, bg: theme.palette.success.bg },
        bad: { fg: theme.palette.error.main, bg: theme.palette.error.bg },
        neutral: { fg: theme.palette.primary.main, bg: theme.palette.info.bg },
        flat: { fg: theme.palette.text.secondary, bg: theme.palette.action.hover },
    }[tone];
    const arrow = !delta || delta.dir === 0 ? '▬' : delta.dir > 0 ? '▲' : '▼';

    // sparkline acumulada no período (a série do backend é diária)
    let sparkSeries = data?.series;
    if (cumulative && sparkSeries) {
        let acc = 0;
        sparkSeries = sparkSeries.map((v) => (acc += v));
    }
    // sem histórico: mostra a nota no lugar da sparkline
    const showNote = !hasTrend;

    const absLabel = !hasTrend
        ? ''
        : kind === 'pct'
            ? `vs. ${format(data.previous_total)}`
            : `${SIGN[delta.dir]}${format(Math.abs(delta.diff))}`;

    return (
        <Card sx={{ borderLeft: hero ? '4px solid' : '3px solid', borderLeftColor: tone === 'flat' ? 'divider' : palette.fg, overflow: 'hidden', ...(large && { display: 'flex', flexDirection: 'column' }) }}>
            <CardContent sx={large
                ? { p: '14px 18px', '&:last-child': { pb: '14px' }, flex: 1, display: 'flex', flexDirection: 'column' }
                : { p: '10px 14px 8px', '&:last-child': { pb: '8px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: '4px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg }}>
                            {icon}
                        </Box>
                        {label}
                        {help && <IndicatorHelp help={help} size={13} />}
                        {estimated && (
                            <Tooltip title={estimatedHint || 'Estimado: reconstruído a partir das movimentações, com preço/custo atuais'}>
                                <Box component="span" sx={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.disabled', border: '1px solid', borderColor: 'divider', borderRadius: '5px', px: '4px', ml: '-1px', cursor: 'help' }}>
                                    est.
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                    {hasTrend && !loading && (
                        <Box sx={{ fontSize: '10.5px', fontWeight: 700, px: '7px', py: '1px', borderRadius: '20px', color: palette.fg, bgcolor: palette.bg, display: 'inline-flex', gap: '3px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {arrow} {delta.label}
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <>
                        <Skeleton variant="text" width={110} height={28} />
                        <Skeleton variant="rounded" height={large ? 88 : H} sx={{ my: '4px' }} />
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: large ? { xs: '26px', sm: '30px' } : '20px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                                {valueText != null ? valueText : total == null ? '—' : format(total)}
                            </Typography>
                            {hasTrend && (
                                <Typography title="vs. período anterior" sx={{ fontSize: '10.5px', fontWeight: 600, color: palette.fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                    {absLabel}
                                </Typography>
                            )}
                        </Box>

                        {hideSpark ? (
                            <Box sx={{ height: 6 }} />
                        ) : !showNote ? (
                            <Box sx={{ color: 'text.disabled' }}>
                                <Sparkline series={sparkSeries} daily={cumulative ? data.series : undefined} days={days} height={large ? 88 : H} color={palette.fg} bg={palette.bg} format={format} uid={`tk-${uid}`} />
                            </Box>
                        ) : (
                            <Typography sx={{ fontSize: '10px', color: 'text.disabled', mt: '6px' }}>
                                Posição atual · sem histórico
                            </Typography>
                        )}
                        {hasTrend && note && (
                            <Box sx={{ fontSize: '10.5px', color: 'text.disabled', pt: '6px', mt: '2px', borderTop: '1px dashed', borderColor: 'divider', minHeight: 22, display: 'flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums', '& b': { color: 'text.secondary', fontWeight: 600 } }}>
                                <span>{note}</span>
                            </Box>
                        )}
                        {footer && (
                            <Box sx={{ mt: 'auto', pt: '12px', borderTop: '1px dashed', borderColor: 'divider' }}>
                                {footer}
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
